import { Link } from 'react-router-dom'
import { ShieldAlert } from 'lucide-react'
import { usePermissions } from '@/hooks/usePermissions'

interface PermissionRouteProps {
  /** Required permission to access this route */
  permission?: string
  /** User needs at least one of these */
  anyOf?: string[]
  children: React.ReactNode
}

/**
 * Route guard used in `App.tsx` to keep pages the current admin has no
 * permission for from rendering.
 *
 * It renders an explicit "no access" panel rather than redirecting. A redirect
 * can loop if the target is itself gated, and it leaves the operator guessing
 * why the page they typed bounced. Because this renders inside `AdminLayout`'s
 * `<Outlet/>`, the sidebar and header stay mounted, so the panel is always
 * escapable.
 *
 * This is a UX guard, not an access control: the API authorises every request
 * on its own. `usePermissions` is deliberately permissive for super admins and
 * for users whose permission list the server never sent — see the comment
 * there.
 */
export function PermissionRoute({ permission, anyOf, children }: PermissionRouteProps) {
  const { has, hasAny } = usePermissions()

  let allowed = true
  if (permission) {
    allowed = has(permission)
  } else if (anyOf) {
    allowed = hasAny(...anyOf)
  }

  if (!allowed) {
    const required = permission ?? anyOf?.join(' or ') ?? ''
    return (
      <div
        role="alert"
        className="flex flex-col items-center justify-center gap-3 py-24 text-center"
      >
        <ShieldAlert className="h-10 w-10 text-muted-foreground" aria-hidden="true" />
        <h1 className="text-lg font-semibold text-foreground">You don&rsquo;t have access to this page</h1>
        <p className="max-w-md text-sm text-muted-foreground">
          Your account is missing the <code className="font-mono">{required}</code> permission. Ask a
          super admin to grant it if you need it.
        </p>
        <Link
          to="/dashboard"
          className="mt-2 inline-flex h-9 items-center rounded-md bg-primary px-4 text-sm font-medium text-primary-foreground hover:bg-primary/90"
        >
          Back to dashboard
        </Link>
      </div>
    )
  }

  return <>{children}</>
}
